import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request, checkAccessRequest } from '../../../_network/request'
import { billingAccountServicesURLs } from '../../../_network/apiUrls'
import CustomBackdrop from '../../_common/CustomBackdrop'
import CustomDialog from '../../_common/CustomDialog'
import OpenStackServicesView from './OpenStackServicesView'
import OpenStackServicesEdit from './OpenStackServicesEdit'

const OpenStackServicesWrapper = (props) => {
  const {
    backendServices,
    billingServices,
    subscriptionServices,
    supportedServiceConfigs,
    billingServicesConfigurable,
    setFetchServices,
  } = props
  const theme = useTheme()
  
  const account_id = useSelector(state => state.profile.account_id)
  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [expanded, setExpanded] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)  
  const [errors, setErrors] = useState([])
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)

  const [configs, setConfigs] = useState([])
  const [configOptions, setConfigOptions] = useState([])
  const [identityProviderExists, setIdentityProviderExists] = useState(false)

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.oxfordBlue,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.vodka,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.vodka, 0.8),
    },
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getServiceTitle = (service_name) => {
    const service = supportedServiceConfigs.find(item => item.service === service_name)
    return service ? service.title.value : ''
  }

  const handleErrorDialogOpen = () => {
    setErrorDialogOpen(true)
  }

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false)
  }

  const checkDomainAccess = async (domain) => {
    const response = await checkAccessRequest(domain)    

    if (response && response.status_code && response.status_code < 400) {
      return true
    }

    return false
  }

  const validateBackendForm = () => {
    let errors_list = []

    if (billingServicesConfigurable && !identityProviderExists) {
      errors_list.push({
        title: defaultTexts.identityProviderIsNotSelected,
      })
    }

    let duplicated_region_name = false

    for (let index = 0; index < configs.length; index++) {
      const s = configs[index]
      const options = configOptions.find(item => item.service_name === s.service_name && item.region_name === s.region_name)      
      const same_region = configs.filter(item => item.service_name === s.service_name && item.region_name === s.region_name)
      const check_record = (options && options.billing_new) ? true : false
      const service_title = getServiceTitle(s.service_name)
      
      if (same_region.length > 1) {
        duplicated_region_name = true
      }

      if (check_record && !s.service_domain) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.serviceDomain,
          field_key: s.service + '-' + s.region_name + '-service_domain',
        })
      }

      if (check_record && !s.service_version) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.apiVersion,
          field_key: s.service + '-' + s.region_name + '-api_version',
        })
      }

      if (check_record && !s.region_name) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.regionName,
          field_key: s.service + '-' + s.region_name + '-region_name',
        })
      }

      if (check_record && !s.resource_suspend_policy) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.suspendAction,
          field_key: s.service + '-' + s.region_name + '-resource_suspend_policy',
        })
      }

      if (check_record && !s.resource_suspend_period) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.suspendDelayMinutes,
          field_key: s.service + '-' + s.region_name + '-resource_suspend_period',
        })
      }

      if (check_record && !s.resource_delete_policy) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.finalAction,
          field_key: s.service + '-' + s.region_name + '-resource_delete_policy',
        })
      }

      if (check_record && !s.resource_delete_period) {
        errors_list.push({
          title: service_title + ' - ' + defaultTexts.finalActionDelayMinutes,
          field_key: s.service + '-' + s.region_name + '-resource_delete_period',
        })
      }
      
    }

    if (duplicated_region_name) {
      errors_list.push({
        title: defaultTexts.duplicatedRegionName,
      })
    }

    return errors_list
  }

  const validateFormFields = () => {
    let errors_list = []

    const backend_form_errors = validateBackendForm()
    errors_list = [...errors_list, ...backend_form_errors]

    setErrors(errors_list)
    
    return errors_list.length === 0 ? true : false
  }

  const validateForm = async () => {
    const form_fields_valid = validateFormFields()
    let domains_valid = true

    if (form_fields_valid) {
      let errors_list = []

      for (let index = 0; index < configs.length; index++) {
        const s = configs[index]
        const domain_checked = await checkDomainAccess(s.service_domain)
        if (!domain_checked) {
          domains_valid = false
          errors_list.push({
            title: s.title + ' - ' + defaultTexts.accessDenied,
          })
        }
      }

      setErrors(prev => [...prev, ...errors_list])      
    }

    const result = (form_fields_valid && domains_valid)

    return result
  }

  const billingAccountServicesRequest = async ({ method, postData, record_id }) => {
    let url = constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices

    if (record_id) {
      url = constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices + '/' + record_id
    }

    let request_data = { url, method, headers: { 'customer-account-id': account_id } }

    if (postData) {
      request_data.data = postData
    }
    
    await request(request_data)
  }

  const handleClickEdit = () => {
    setEditMode(true)
  }
  
  const handleClickSave = async () => {
    setIsLoading(true)

    const form_valid = await validateForm()

    if (form_valid) {
      for (let index = 0; index < configs.length; index++) {
        const item = configs[index]
        const record_id = item.id
        const options = configOptions.find(item2 => item2.service_name === item.service_name && item2.region_name === item.region_name)
        
        delete item.id

        if (item.credentials && (!item.credentials.app_auth_id || !item.credentials.app_auth_secret)) {
          delete item.credentials
        }

        if (options.billing_current && options.billing_new) {
          await billingAccountServicesRequest({
            method: 'PUT',
            postData: item,
            record_id,
          })
        }

        if (!options.billing_current && options.billing_new) {
          await billingAccountServicesRequest({
            method: 'POST',
            postData: { ...item, account_id },
          })
        }

        if (options.billing_current && !options.billing_new) {
          await billingAccountServicesRequest({
            method: 'DELETE',
            record_id,
          })
        }
      }

      setFetchServices(prev => prev + 1)
      setEditMode(false)
    } else {
      handleErrorDialogOpen()
    }

    setIsLoading(false)
  }

  useEffect(() => {
    let _configs = []
    let config_options = []
    
    if (backendServices.length > 0) {
      backendServices.forEach(b => {
        const region_name = b.config_params.region_name ? b.config_params.region_name : b.config_params.default_region
        const billing_service = billingServices.find(item => item.service_name === b.service && item.region_name === region_name)

        let config_options_item = {
          service_name: b.service,
          region_name,
        }
        
        if (billing_service) {
          _configs.push({
            id: billing_service.id,
            is_identity_provider: billing_service.is_identity_provider,
            service_name: billing_service.service_name,
            service_domain: billing_service.service_domain,
            service_version: billing_service.service_version,
            region_name: billing_service.region_name,
            resource_suspend_period: billing_service.resource_suspend_period,
            resource_delete_period: billing_service.resource_delete_period,
            resource_suspend_policy: billing_service.resource_suspend_policy,
            resource_delete_policy: billing_service.resource_delete_policy,
            credentials: {
              app_auth_id: '',
              app_auth_secret: '',
            }
          })

          config_options_item.billing_current = true
          config_options_item.billing_new = true
        } else {
          _configs.push({
            id: null,
            is_identity_provider: b.config_params.is_identity_provider,
            service_name: b.service,
            service_domain: b.config_params.service_domain,
            service_version: b.config_params.api_version,
            region_name: region_name,
            resource_suspend_period: '',
            resource_delete_period: '',
            resource_suspend_policy: '',
            resource_delete_policy: '',
            credentials: {
              app_auth_id: '',
              app_auth_secret: '',
            }  
          })

          config_options_item.billing_current = false
          config_options_item.billing_new = false
        }

        config_options.push(config_options_item)
      })

      setConfigs(_configs)
      setConfigOptions(config_options)
    } else if (billingServices.length > 0) {
      billingServices.forEach(b => {
        config_options.push({
          service_name: b.service_name,
          region_name: b.region_name,
          billing_current: true,
          billing_new: true,
        })
      })

      setConfigs([...billingServices])
      setConfigOptions(config_options)
    } else {
      subscriptionServices.forEach(b => {
        _configs.push({
          id: null,
          is_identity_provider: b.service === 'identity_keystone' ? true : false,
          service_name: b.service,
          service_domain: '',
          service_version: '',
          region_name: 'Region-1',
          resource_suspend_period: '',
          resource_delete_period: '',
          resource_suspend_policy: '',
          resource_delete_policy: '',
          credentials: {
            app_auth_id: '',
            app_auth_secret: '',
          }  
        })

        config_options.push({
          service_name: b.service,
          region_name: 'Region-1',
          billing_current: false,
          billing_new: true,
        })
      })

      setConfigs(_configs)
      setConfigOptions(config_options)
    }
  }, [backendServices, billingServices, subscriptionServices])

  useEffect(() => {
    const identity_service = configs.find(item => item.is_identity_provider)
    setIdentityProviderExists(identity_service ? true : false)
  }, [configs])
  
  return (
    <Box sx={wrapperStyle}>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={headStyle}
      >
        <Box sx={labelStyle}>
          <Box>{defaultTexts.openStackServices}</Box>
        </Box>
        <Box sx={{ paddingRight: '10px ' }}>
          {
            expanded ?
            <IconButton onClick={() => setExpanded(false)}>
              <FaMinimize color={theme.palette.greenSheen} />
            </IconButton> :
            <IconButton onClick={() => setExpanded(true)}>
              <FaMaximize color={theme.palette.greenSheen} />
            </IconButton>
          }
        </Box>
      </Stack>

      {
        expanded &&
        <Box sx={bodyStyle}>
          {
            editMode ?
            <OpenStackServicesEdit
              configs={configs}
              setConfigs={setConfigs}
              configOptions={configOptions}
              setConfigOptions={setConfigOptions}
              supportedServiceConfigs={supportedServiceConfigs}
              billingServicesConfigurable={billingServicesConfigurable}
              identityProviderExists={identityProviderExists}
              validateFormFields={validateFormFields}
              errors={errors}
            /> :
            <OpenStackServicesView
              backendServices={backendServices}
              billingServices={billingServices}
              subscriptionServices={subscriptionServices}
              supportedServiceConfigs={supportedServiceConfigs}
            />
          }

          {
            editPermission &&
            <Stack
              direction='row'
              justifyContent='flex-end'
            >
              {
                editMode ?
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickSave}
                  sx={saveButtonStyle}
                >
                  {defaultTexts.save}
                </Button> :
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickEdit}
                  sx={editButtonStyle}
                >
                  {defaultTexts.edit}
                </Button>
              }            
            </Stack>
          }
        </Box>
      }

      <CustomDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        dialogTitle={{ title: defaultTexts.formValidationError }}
        dialogBody={{
          text: defaultTexts.formValidationErrorText,
          sx: { paddingBottom: '20px' }
        }}
      >
        {
          errors.map((item, index) => (
            <Box key={index}>{item.title}</Box>
          ))
        }
      </CustomDialog>
    </Box>
  )
}

export default OpenStackServicesWrapper